/**
 * Tabela do painel (F1.8) — lista densa, legível e navegável por teclado (docs/03 §2 A02).
 *
 * Regras que este arquivo carrega:
 *  - piso de legibilidade de tabela = 12 px (`--font-size-tabela`, decisão 1.9.3). O painel é lido
 *    de pé, no balcão: menor que isso vira adivinhação;
 *  - **a tabela sempre diz o que ela é** (`<caption>`), porque quem usa leitor de tela chega nela
 *    sem o contexto visual da tela;
 *  - lista sem linhas não é uma tabela vazia e muda: é o estado "vazio" da tabela A, com o mesmo
 *    texto de todo o produto (`EstadoDaTela`);
 *  - rolagem horizontal fica presa dentro da tabela e alcançável por teclado — a página nunca rola
 *    de lado.
 */
import type { CSSProperties, ReactNode } from 'react';

import { EstadoDaTela } from './EstadoDaTela.js';

export interface ColunaTabela<T> {
  /** Identificador estável da coluna (usado como `key` e no cabeçalho). */
  chave: string;
  titulo: string;
  celula: (linha: T) => ReactNode;
  /** Números e ações ficam melhor alinhados à direita. */
  alinharAoFim?: boolean | undefined;
  larguraMinima?: string | undefined;
}

export interface TabelaProps<T> {
  /** O que esta tabela lista, em uma frase (vira o `<caption>`). */
  legenda: string;
  colunas: readonly ColunaTabela<T>[];
  linhas: readonly T[];
  chaveDaLinha: (linha: T) => string;
  /** Esconde a legenda visualmente; ela continua existindo para o leitor de tela. */
  legendaVisivel?: boolean | undefined;
  /** Substitui o bloco padrão do estado "vazio" (ex.: com o atalho de criar da tela). */
  vazio?: ReactNode | undefined;
}

/** Esconde na tela sem esconder do leitor de tela. */
const SO_PARA_LEITOR: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const CELULA: CSSProperties = {
  padding: 'var(--esp-3)',
  borderBottom: '1px solid var(--cor-linha)',
  verticalAlign: 'top',
  lineHeight: 'var(--altura-linha-normal)',
};

export function Tabela<T>({
  legenda,
  colunas,
  linhas,
  chaveDaLinha,
  legendaVisivel = true,
  vazio,
}: TabelaProps<T>) {
  return (
    <div
      // `role="region"` + `tabIndex` para quem rola a tabela pelo teclado quando ela é mais larga
      // que a tela (celular do gestor, tablet no balcão).
      role="region"
      aria-label={legenda}
      tabIndex={0}
      style={{
        overflowX: 'auto',
        border: '1px solid var(--cor-linha)',
        borderRadius: 'var(--raio-sm)',
        background: 'var(--cor-superficie)',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--font-size-tabela)',
          color: 'var(--cor-texto)',
        }}
      >
        <caption
          style={
            legendaVisivel
              ? {
                  captionSide: 'top',
                  textAlign: 'start',
                  padding: 'var(--esp-3)',
                  color: 'var(--cor-texto-suave)',
                  fontSize: 'var(--font-size-formulario)',
                  fontWeight: 'var(--peso-medio)',
                }
              : SO_PARA_LEITOR
          }
        >
          {legenda}
        </caption>

        <thead>
          <tr>
            {colunas.map((coluna) => (
              <th
                key={coluna.chave}
                scope="col"
                style={{
                  ...CELULA,
                  textAlign: coluna.alinharAoFim ? 'end' : 'start',
                  minWidth: coluna.larguraMinima,
                  background: 'var(--cor-superficie-2)',
                  borderBottom: '1px solid var(--cor-linha-forte)',
                  color: 'var(--cor-texto-suave)',
                  fontWeight: 'var(--peso-forte)',
                  whiteSpace: 'nowrap',
                }}
              >
                {coluna.titulo}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {linhas.length === 0 ? (
            <tr>
              <td colSpan={Math.max(colunas.length, 1)} style={{ ...CELULA, borderBottom: 'none' }}>
                {/* Sem linhas = estado "vazio" da tabela A, com o texto único do produto. */}
                {vazio ?? <EstadoDaTela estado="vazio" />}
              </td>
            </tr>
          ) : (
            linhas.map((linha) => (
              <tr key={chaveDaLinha(linha)}>
                {colunas.map((coluna) => (
                  <td
                    key={coluna.chave}
                    style={{
                      ...CELULA,
                      textAlign: coluna.alinharAoFim ? 'end' : 'start',
                      minWidth: coluna.larguraMinima,
                    }}
                  >
                    {coluna.celula(linha)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
